@file:Suppress("JSON_FORMAT_REDUNDANT_DEFAULT", "RedundantVisibilityModifier")

package com.steamstreet.vegasful.graphql.api

import com.steamstreet.dates.DurationSerializer
import com.steamstreet.graphkt.ID
import kotlin.Boolean
import kotlin.Float
import kotlin.Int
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlin.time.Duration
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime
import kotlinx.datetime.serializers.InstantIso8601Serializer
import kotlinx.datetime.serializers.LocalDateIso8601Serializer
import kotlinx.datetime.serializers.LocalTimeIso8601Serializer
import kotlinx.serialization.Contextual
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.Json
import kotlinx.serialization.modules.SerializersModule

/**
 *  The input to a conversation
 */
@Serializable
public data class ConversationInput(
  public val query: String,
  public val location: GPSCoordinatesInput? = null,
)

/**
 *  GPS Coordinates input
 */
@Serializable
public data class GPSCoordinatesInput(
  public val latitude: Float,
  public val longitude: Float,
)

@Serializable
public data class GlobalSearchInput(
  public val text: String,
)

@Serializable
public data class VariantInput(
  public val variants: List<ImageVariant>? = null,
)

@Serializable
public data class PerformerSearch(
  public val text: String? = null,
)

@Serializable
public data class ArticleSearchInput(
  public val maxResults: Int? = null,
  public val links: List<String>? = null,
)

@Serializable
public data class EventSearch(
  @Contextual
  public val start: AWSDateTime? = null,
  @Contextual
  public val end: AWSDateTime? = null,
  public val pageSize: Int? = null,
  public val pageToken: String? = null,
  public val minScore: Int? = null,
)

@Serializable
public data class EventAttributeInput(
  public val key: String,
  public val `value`: String,
)

@Serializable
public data class SystemEventInput(
  public val attributes: List<EventAttributeInput>,
)

@Serializable
public data class SubscriptionInput(
  public val userId: String? = null,
  public val entityPath: String,
)

@Serializable
public data class ClearUserDataInput(
  public val userEmail: String? = null,
)

@Serializable
public data class InteractInput(
  public val type: InteractionType,
  public val entityPath: String,
)

@Serializable
public data class DeviceTokenRegistration(
  public val platform: DeviceTokenPlatform,
  public val token: String,
)

@Serializable
public data class LikeInput(
  public val entityPath: String,
  public val additionalPaths: List<String>? = null,
)

@Serializable
public data class ShareInput(
  public val entityPath: String,
)

/**
 *  Additional
 */
@Serializable
public data class ViewQuery(
  @Contextual
  public val startDate: AWSDate? = null,
  @Contextual
  public val endDate: AWSDate? = null,
)

/**
 *  Input for creating an anonymous account.
 */
@Serializable
public data class CreateAnonymousInput(
  public val device: DeviceTokenRegistration? = null,
)

@Serializable
public data class LogQueryInput(
  public val query: String,
  public val logGroups: List<String>? = null,
  @Contextual
  public val start: AWSDateTime? = null,
  @Contextual
  public val end: AWSDateTime? = null,
  public val limit: Int? = null,
)

@Serializable
public data class ImageUpload(
  public val `data`: String? = null,
  public val type: String? = null,
  public val uri: String? = null,
)

/**
 *  The update information
 */
@Serializable
public data class VenueUpdateInput(
  public val name: String? = null,
  public val description: String? = null,
  public val address: AddressInput? = null,
  public val location: GPSCoordinatesInput? = null,
  public val links: List<SocialLinkInput>? = null,
  public val ticketsUrl: String? = null,
  public val parent: String? = null,
  public val tags: List<String>? = null,
  public val images: List<String>? = null,
  public val aliases: List<String>? = null,
  public val redirect: String? = null,
  public val status: VenueStatus? = null,
  public val rankingScore: Int? = null,
  public val eventStreamId: String? = null,
  public val parking: String? = null,
  public val contentView: String? = null,
)

@Serializable
public data class SocialLinkInput(
  public val type: String,
  public val locator: String? = null,
)

@Serializable
public data class AddressInput(
  public val street: String? = null,
  public val city: String? = null,
  public val state: String? = null,
  public val zip: String? = null,
  public val country: String? = null,
)

/**
 *  The update information
 */
@Serializable
public data class PerformerUpdateInput(
  public val name: String? = null,
  public val description: String? = null,
  public val aliases: List<String>? = null,
  public val tags: List<String>? = null,
  public val images: List<String>? = null,
  public val wikiIdentifier: String? = null,
  public val rankingScore: Int? = null,
)

@Serializable
public data class MergeInput(
  public val target: ID,
  public val sources: List<ID>,
)

@Serializable
public data class EventUpdateInput(
  public val parent: String? = null,
  public val name: String? = null,
  public val description: String? = null,
  public val venue: String? = null,
  @Contextual
  public val date: AWSDate? = null,
  @Contextual
  public val time: AWSTime? = null,
  public val duration: Int? = null,
  @Contextual
  public val endDate: AWSDate? = null,
  @Contextual
  public val endTime: AWSTime? = null,
  public val ticketsUrl: String? = null,
  public val sourceUrl: String? = null,
  public val aliases: List<String>? = null,
  public val images: List<String>? = null,
  public val excludedImages: List<String>? = null,
  public val excludedTags: List<String>? = null,
  public val performers: List<String>? = null,
  public val excluded: Boolean? = null,
  public val rankingScore: Int? = null,
  public val suggestedPerformers: List<String>? = null,
  public val eventTags: List<EventTagInput>? = null,
  public val contentView: String? = null,
)

@Serializable
public data class EventTagInput(
  public val id: ID,
  public val confidence: Int? = null,
)

@Serializable
public data class ArticleInput(
  public val title: String? = null,
  public val summary: String? = null,
  public val content: String? = null,
  public val images: List<String>? = null,
  public val links: List<String>? = null,
)

@Serializable
public data class TagUpdateInput(
  public val name: String? = null,
  public val parent: String? = null,
  public val description: String? = null,
  public val images: List<String>? = null,
  public val shadow: Boolean? = null,
  public val implied: List<String>? = null,
  public val pageTitle: String? = null,
  public val rankingScore: Int? = null,
  public val aiPromptInput: String? = null,
  public val contentView: String? = null,
)

@Serializable
public data class LoaderConfiguration(
  public val name: String? = null,
  public val module: ID? = null,
  public val schedule: String? = null,
  public val parameters: String? = null,
)

@Serializable
public data class LoaderPublishInput(
  public val `data`: String,
  public val incomplete: Boolean? = null,
)

@Serializable
public data class LoaderExecutionInput(
  public val `data`: String? = null,
)

/**
 *  defines a content panel within a view
 */
@Serializable
public data class ContentPanelInput(
  public val type: ContentPanelType,
  public val title: String? = null,
  public val titleLink: String? = null,
  public val subTitle: String? = null,
  public val subTitleLink: String? = null,
  public val images: List<ID>? = null,
  public val content: String? = null,
  public val entities: List<String>? = null,
  public val entityFilters: List<String>? = null,
  public val filterTags: List<String>? = null,
  public val entitiesSource: EntitySource? = null,
  public val source: PanelSourceInput? = null,
  public val articleSource: String? = null,
  public val displayOrder: DisplayOrder? = null,
  public val links: List<ContentViewLinkInput>? = null,
  public val flags: List<ContentPanelFlag>? = null,
  public val minWidth: Int? = null,
  public val maxWidth: Int? = null,
)

/**
 *  A date range for an event query, either a relative preset or explicit bounds.
 */
@Serializable
public data class DateRangeInput(
  public val preset: DateRangePreset? = null,
  public val from: String? = null,
  public val to: String? = null,
)

/**
 *  The query parameters for an event source.
 */
@Serializable
public data class EventQueryInput(
  public val venues: List<String>? = null,
  public val tags: List<String>? = null,
  public val dateRange: DateRangeInput? = null,
  public val minScore: Float? = null,
  public val sort: SortOrder? = null,
  public val limit: Int? = null,
)

/**
 *  Defines where a panel's entities come from.
 */
@Serializable
public data class PanelSourceInput(
  public val entityType: SourceEntityType,
  public val mode: SourceMode,
  public val manual: List<String>? = null,
  public val eventQuery: EventQueryInput? = null,
  public val eventQueries: List<EventQueryInput>? = null,
  public val sort: SortOrder? = null,
  public val limit: Int? = null,
  public val minimum: Int? = null,
  public val deduplicate: Boolean? = null,
)

/**
 *  A link to display on the content view.
 */
@Serializable
public data class ContentViewLinkInput(
  public val path: String,
  public val name: String,
  public val description: String? = null,
  public val type: ContentViewLinkType,
)

public object ConversationStatusSerializer : KSerializer<ConversationStatus> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ConversationStatus", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ConversationStatus) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ConversationStatus = ConversationStatus.valueOf(decoder.decodeString())
}

@Serializable(with = ConversationStatusSerializer::class)
public sealed class ConversationStatus(
  public val name: String,
) {
  override fun toString(): String = name

  public object RUNNING : ConversationStatus("RUNNING")

  public object COMPLETE : ConversationStatus("COMPLETE")

  public object FAILED : ConversationStatus("FAILED")

  public class Unknown(
    name: String,
  ) : ConversationStatus(name)

  public companion object {
    public val entries: List<ConversationStatus>
      get() = listOf(RUNNING, COMPLETE, FAILED)

    public fun valueOf(name: String): ConversationStatus = when (name) {
      "RUNNING" -> RUNNING
      "COMPLETE" -> COMPLETE
      "FAILED" -> FAILED
      else -> Unknown(name)
    }
  }
}

public object VenueStatusSerializer : KSerializer<VenueStatus> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("VenueStatus", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: VenueStatus) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): VenueStatus = VenueStatus.valueOf(decoder.decodeString())
}

@Serializable(with = VenueStatusSerializer::class)
public sealed class VenueStatus(
  public val name: String,
) {
  override fun toString(): String = name

  public object OPEN : VenueStatus("OPEN")

  public object CLOSED_TEMPORARILY : VenueStatus("CLOSED_TEMPORARILY")

  public object CLOSED_PERMANENTLY : VenueStatus("CLOSED_PERMANENTLY")

  public class Unknown(
    name: String,
  ) : VenueStatus(name)

  public companion object {
    public val entries: List<VenueStatus>
      get() = listOf(OPEN, CLOSED_TEMPORARILY, CLOSED_PERMANENTLY)

    public fun valueOf(name: String): VenueStatus = when (name) {
      "OPEN" -> OPEN
      "CLOSED_TEMPORARILY" -> CLOSED_TEMPORARILY
      "CLOSED_PERMANENTLY" -> CLOSED_PERMANENTLY
      else -> Unknown(name)
    }
  }
}

public object ImageVariantSerializer : KSerializer<ImageVariant> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ImageVariant", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ImageVariant) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ImageVariant = ImageVariant.valueOf(decoder.decodeString())
}

@Serializable(with = ImageVariantSerializer::class)
public sealed class ImageVariant(
  public val name: String,
) {
  override fun toString(): String = name

  public object SMALL : ImageVariant("SMALL")

  public object MEDIUM : ImageVariant("MEDIUM")

  public object LARGE : ImageVariant("LARGE")

  public class Unknown(
    name: String,
  ) : ImageVariant(name)

  public companion object {
    public val entries: List<ImageVariant>
      get() = listOf(SMALL, MEDIUM, LARGE)

    public fun valueOf(name: String): ImageVariant = when (name) {
      "SMALL" -> SMALL
      "MEDIUM" -> MEDIUM
      "LARGE" -> LARGE
      else -> Unknown(name)
    }
  }
}

public object InteractionTypeSerializer : KSerializer<InteractionType> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("InteractionType", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: InteractionType) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): InteractionType = InteractionType.valueOf(decoder.decodeString())
}

@Serializable(with = InteractionTypeSerializer::class)
public sealed class InteractionType(
  public val name: String,
) {
  override fun toString(): String = name

  public object LIKE : InteractionType("LIKE")

  public object FOLLOW : InteractionType("FOLLOW")

  public object SHARE : InteractionType("SHARE")

  public object CALENDAR : InteractionType("CALENDAR")

  public object TICKETS : InteractionType("TICKETS")

  public object MORE_INFO : InteractionType("MORE_INFO")

  public object VIEWED : InteractionType("VIEWED")

  public class Unknown(
    name: String,
  ) : InteractionType(name)

  public companion object {
    public val entries: List<InteractionType>
      get() = listOf(LIKE, FOLLOW, SHARE, CALENDAR, TICKETS, MORE_INFO, VIEWED)

    public fun valueOf(name: String): InteractionType = when (name) {
      "LIKE" -> LIKE
      "FOLLOW" -> FOLLOW
      "SHARE" -> SHARE
      "CALENDAR" -> CALENDAR
      "TICKETS" -> TICKETS
      "MORE_INFO" -> MORE_INFO
      "VIEWED" -> VIEWED
      else -> Unknown(name)
    }
  }
}

public object DeviceTokenPlatformSerializer : KSerializer<DeviceTokenPlatform> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("DeviceTokenPlatform", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: DeviceTokenPlatform) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): DeviceTokenPlatform = DeviceTokenPlatform.valueOf(decoder.decodeString())
}

@Serializable(with = DeviceTokenPlatformSerializer::class)
public sealed class DeviceTokenPlatform(
  public val name: String,
) {
  override fun toString(): String = name

  public object IOS : DeviceTokenPlatform("IOS")

  public object ANDROID : DeviceTokenPlatform("ANDROID")

  public class Unknown(
    name: String,
  ) : DeviceTokenPlatform(name)

  public companion object {
    public val entries: List<DeviceTokenPlatform>
      get() = listOf(IOS, ANDROID)

    public fun valueOf(name: String): DeviceTokenPlatform = when (name) {
      "IOS" -> IOS
      "ANDROID" -> ANDROID
      else -> Unknown(name)
    }
  }
}

public object ContentViewTypeSerializer : KSerializer<ContentViewType> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ContentViewType", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ContentViewType) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ContentViewType = ContentViewType.valueOf(decoder.decodeString())
}

@Serializable(with = ContentViewTypeSerializer::class)
public sealed class ContentViewType(
  public val name: String,
) {
  override fun toString(): String = name

  public object ENTITY_SCROLLER : ContentViewType("ENTITY_SCROLLER")

  public object ENTITY_LIST : ContentViewType("ENTITY_LIST")

  public object EVENT_CALENDAR : ContentViewType("EVENT_CALENDAR")

  public object ARTICLE : ContentViewType("ARTICLE")

  public object LINKS : ContentViewType("LINKS")

  public object HERO : ContentViewType("HERO")

  public class Unknown(
    name: String,
  ) : ContentViewType(name)

  public companion object {
    public val entries: List<ContentViewType>
      get() = listOf(ENTITY_SCROLLER, ENTITY_LIST, EVENT_CALENDAR, ARTICLE, LINKS, HERO)

    public fun valueOf(name: String): ContentViewType = when (name) {
      "ENTITY_SCROLLER" -> ENTITY_SCROLLER
      "ENTITY_LIST" -> ENTITY_LIST
      "EVENT_CALENDAR" -> EVENT_CALENDAR
      "ARTICLE" -> ARTICLE
      "LINKS" -> LINKS
      "HERO" -> HERO
      else -> Unknown(name)
    }
  }
}

public object ContentSourceSerializer : KSerializer<ContentSource> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ContentSource", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ContentSource) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ContentSource = ContentSource.valueOf(decoder.decodeString())
}

@Serializable(with = ContentSourceSerializer::class)
public sealed class ContentSource(
  public val name: String,
) {
  override fun toString(): String = name

  public object FOR_YOU : ContentSource("FOR_YOU")

  public class Unknown(
    name: String,
  ) : ContentSource(name)

  public companion object {
    public val entries: List<ContentSource>
      get() = listOf(FOR_YOU)

    public fun valueOf(name: String): ContentSource = when (name) {
      "FOR_YOU" -> FOR_YOU
      else -> Unknown(name)
    }
  }
}

public object ContentPanelTypeSerializer : KSerializer<ContentPanelType> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ContentPanelType", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ContentPanelType) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ContentPanelType = ContentPanelType.valueOf(decoder.decodeString())
}

@Serializable(with = ContentPanelTypeSerializer::class)
public sealed class ContentPanelType(
  public val name: String,
) {
  override fun toString(): String = name

  public object HERO : ContentPanelType("HERO")

  public object ENTITY_SCROLLER : ContentPanelType("ENTITY_SCROLLER")

  public object ENTITY_LIST : ContentPanelType("ENTITY_LIST")

  public object TOP_LIST : ContentPanelType("TOP_LIST")

  public object EVENT_CALENDAR : ContentPanelType("EVENT_CALENDAR")

  public object EVENT_IMAGE_CALENDAR : ContentPanelType("EVENT_IMAGE_CALENDAR")

  public object ARTICLE : ContentPanelType("ARTICLE")

  public object PILL_CONTAINER : ContentPanelType("PILL_CONTAINER")

  public object SEARCH : ContentPanelType("SEARCH")

  public object WEEK_SELECTOR : ContentPanelType("WEEK_SELECTOR")

  public object ENTITY_CARD : ContentPanelType("ENTITY_CARD")

  public class Unknown(
    name: String,
  ) : ContentPanelType(name)

  public companion object {
    public val entries: List<ContentPanelType>
      get() = listOf(HERO, ENTITY_SCROLLER, ENTITY_LIST, TOP_LIST, EVENT_CALENDAR, EVENT_IMAGE_CALENDAR, ARTICLE, PILL_CONTAINER, SEARCH, WEEK_SELECTOR, ENTITY_CARD)

    public fun valueOf(name: String): ContentPanelType = when (name) {
      "HERO" -> HERO
      "ENTITY_SCROLLER" -> ENTITY_SCROLLER
      "ENTITY_LIST" -> ENTITY_LIST
      "TOP_LIST" -> TOP_LIST
      "EVENT_CALENDAR" -> EVENT_CALENDAR
      "EVENT_IMAGE_CALENDAR" -> EVENT_IMAGE_CALENDAR
      "ARTICLE" -> ARTICLE
      "PILL_CONTAINER" -> PILL_CONTAINER
      "SEARCH" -> SEARCH
      "WEEK_SELECTOR" -> WEEK_SELECTOR
      "ENTITY_CARD" -> ENTITY_CARD
      else -> Unknown(name)
    }
  }
}

public object EntitySourceSerializer : KSerializer<EntitySource> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("EntitySource", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: EntitySource) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): EntitySource = EntitySource.valueOf(decoder.decodeString())
}

@Serializable(with = EntitySourceSerializer::class)
public sealed class EntitySource(
  public val name: String,
) {
  override fun toString(): String = name

  public object RECOMMENDED : EntitySource("RECOMMENDED")

  public class Unknown(
    name: String,
  ) : EntitySource(name)

  public companion object {
    public val entries: List<EntitySource>
      get() = listOf(RECOMMENDED)

    public fun valueOf(name: String): EntitySource = when (name) {
      "RECOMMENDED" -> RECOMMENDED
      else -> Unknown(name)
    }
  }
}

public object SourceEntityTypeSerializer : KSerializer<SourceEntityType> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("SourceEntityType", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: SourceEntityType) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): SourceEntityType = SourceEntityType.valueOf(decoder.decodeString())
}

@Serializable(with = SourceEntityTypeSerializer::class)
public sealed class SourceEntityType(
  public val name: String,
) {
  override fun toString(): String = name

  public object EVENT : SourceEntityType("EVENT")

  public object VENUE : SourceEntityType("VENUE")

  public object TAG : SourceEntityType("TAG")

  public object PERFORMER : SourceEntityType("PERFORMER")

  public class Unknown(
    name: String,
  ) : SourceEntityType(name)

  public companion object {
    public val entries: List<SourceEntityType>
      get() = listOf(EVENT, VENUE, TAG, PERFORMER)

    public fun valueOf(name: String): SourceEntityType = when (name) {
      "EVENT" -> EVENT
      "VENUE" -> VENUE
      "TAG" -> TAG
      "PERFORMER" -> PERFORMER
      else -> Unknown(name)
    }
  }
}

public object SourceModeSerializer : KSerializer<SourceMode> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("SourceMode", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: SourceMode) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): SourceMode = SourceMode.valueOf(decoder.decodeString())
}

@Serializable(with = SourceModeSerializer::class)
public sealed class SourceMode(
  public val name: String,
) {
  override fun toString(): String = name

  public object MANUAL : SourceMode("MANUAL")

  public object QUERY : SourceMode("QUERY")

  public class Unknown(
    name: String,
  ) : SourceMode(name)

  public companion object {
    public val entries: List<SourceMode>
      get() = listOf(MANUAL, QUERY)

    public fun valueOf(name: String): SourceMode = when (name) {
      "MANUAL" -> MANUAL
      "QUERY" -> QUERY
      else -> Unknown(name)
    }
  }
}

public object DateRangePresetSerializer : KSerializer<DateRangePreset> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("DateRangePreset", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: DateRangePreset) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): DateRangePreset = DateRangePreset.valueOf(decoder.decodeString())
}

@Serializable(with = DateRangePresetSerializer::class)
public sealed class DateRangePreset(
  public val name: String,
) {
  override fun toString(): String = name

  public object TODAY : DateRangePreset("TODAY")

  public object NEXT_24_HOURS : DateRangePreset("NEXT_24_HOURS")

  public object THIS_WEEKEND : DateRangePreset("THIS_WEEKEND")

  public object NEXT_7_DAYS : DateRangePreset("NEXT_7_DAYS")

  public object ALL_UPCOMING : DateRangePreset("ALL_UPCOMING")

  public class Unknown(
    name: String,
  ) : DateRangePreset(name)

  public companion object {
    public val entries: List<DateRangePreset>
      get() = listOf(TODAY, NEXT_24_HOURS, THIS_WEEKEND, NEXT_7_DAYS, ALL_UPCOMING)

    public fun valueOf(name: String): DateRangePreset = when (name) {
      "TODAY" -> TODAY
      "NEXT_24_HOURS" -> NEXT_24_HOURS
      "THIS_WEEKEND" -> THIS_WEEKEND
      "NEXT_7_DAYS" -> NEXT_7_DAYS
      "ALL_UPCOMING" -> ALL_UPCOMING
      else -> Unknown(name)
    }
  }
}

public object SortOrderSerializer : KSerializer<SortOrder> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("SortOrder", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: SortOrder) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): SortOrder = SortOrder.valueOf(decoder.decodeString())
}

@Serializable(with = SortOrderSerializer::class)
public sealed class SortOrder(
  public val name: String,
) {
  override fun toString(): String = name

  public object SCORE : SortOrder("SCORE")

  public object DATE : SortOrder("DATE")

  public class Unknown(
    name: String,
  ) : SortOrder(name)

  public companion object {
    public val entries: List<SortOrder>
      get() = listOf(SCORE, DATE)

    public fun valueOf(name: String): SortOrder = when (name) {
      "SCORE" -> SCORE
      "DATE" -> DATE
      else -> Unknown(name)
    }
  }
}

public object DisplayOrderSerializer : KSerializer<DisplayOrder> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("DisplayOrder", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: DisplayOrder) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): DisplayOrder = DisplayOrder.valueOf(decoder.decodeString())
}

@Serializable(with = DisplayOrderSerializer::class)
public sealed class DisplayOrder(
  public val name: String,
) {
  override fun toString(): String = name

  public object SORTED : DisplayOrder("SORTED")

  public object RANDOM : DisplayOrder("RANDOM")

  public object DATE : DisplayOrder("DATE")

  public class Unknown(
    name: String,
  ) : DisplayOrder(name)

  public companion object {
    public val entries: List<DisplayOrder>
      get() = listOf(SORTED, RANDOM, DATE)

    public fun valueOf(name: String): DisplayOrder = when (name) {
      "SORTED" -> SORTED
      "RANDOM" -> RANDOM
      "DATE" -> DATE
      else -> Unknown(name)
    }
  }
}

public object ContentPanelFlagSerializer : KSerializer<ContentPanelFlag> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ContentPanelFlag", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ContentPanelFlag) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ContentPanelFlag = ContentPanelFlag.valueOf(decoder.decodeString())
}

@Serializable(with = ContentPanelFlagSerializer::class)
public sealed class ContentPanelFlag(
  public val name: String,
) {
  override fun toString(): String = name

  public object MAIN_TITLE_HERO : ContentPanelFlag("MAIN_TITLE_HERO")

  public object HIDE_DATES : ContentPanelFlag("HIDE_DATES")

  public object HIDE_TIMES : ContentPanelFlag("HIDE_TIMES")

  public object SHOW_DATES : ContentPanelFlag("SHOW_DATES")

  public class Unknown(
    name: String,
  ) : ContentPanelFlag(name)

  public companion object {
    public val entries: List<ContentPanelFlag>
      get() = listOf(MAIN_TITLE_HERO, HIDE_DATES, HIDE_TIMES, SHOW_DATES)

    public fun valueOf(name: String): ContentPanelFlag = when (name) {
      "MAIN_TITLE_HERO" -> MAIN_TITLE_HERO
      "HIDE_DATES" -> HIDE_DATES
      "HIDE_TIMES" -> HIDE_TIMES
      "SHOW_DATES" -> SHOW_DATES
      else -> Unknown(name)
    }
  }
}

public object ContentViewLinkTypeSerializer : KSerializer<ContentViewLinkType> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ContentViewLinkType", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ContentViewLinkType) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ContentViewLinkType = ContentViewLinkType.valueOf(decoder.decodeString())
}

@Serializable(with = ContentViewLinkTypeSerializer::class)
public sealed class ContentViewLinkType(
  public val name: String,
) {
  override fun toString(): String = name

  public object NORMAL : ContentViewLinkType("NORMAL")

  public object DATE_SELECT : ContentViewLinkType("DATE_SELECT")

  public class Unknown(
    name: String,
  ) : ContentViewLinkType(name)

  public companion object {
    public val entries: List<ContentViewLinkType>
      get() = listOf(NORMAL, DATE_SELECT)

    public fun valueOf(name: String): ContentViewLinkType = when (name) {
      "NORMAL" -> NORMAL
      "DATE_SELECT" -> DATE_SELECT
      else -> Unknown(name)
    }
  }
}

public object ContentViewEntityActionSerializer : KSerializer<ContentViewEntityAction> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("ContentViewEntityAction", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: ContentViewEntityAction) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): ContentViewEntityAction = ContentViewEntityAction.valueOf(decoder.decodeString())
}

@Serializable(with = ContentViewEntityActionSerializer::class)
public sealed class ContentViewEntityAction(
  public val name: String,
) {
  override fun toString(): String = name

  public object DATE_SELECT : ContentViewEntityAction("DATE_SELECT")

  public class Unknown(
    name: String,
  ) : ContentViewEntityAction(name)

  public companion object {
    public val entries: List<ContentViewEntityAction>
      get() = listOf(DATE_SELECT)

    public fun valueOf(name: String): ContentViewEntityAction = when (name) {
      "DATE_SELECT" -> DATE_SELECT
      else -> Unknown(name)
    }
  }
}

public object SocialImageStateSerializer : KSerializer<SocialImageState> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("SocialImageState", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: SocialImageState) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): SocialImageState = SocialImageState.valueOf(decoder.decodeString())
}

@Serializable(with = SocialImageStateSerializer::class)
public sealed class SocialImageState(
  public val name: String,
) {
  override fun toString(): String = name

  public object PENDING : SocialImageState("PENDING")

  public object READY : SocialImageState("READY")

  public object FAILED : SocialImageState("FAILED")

  public class Unknown(
    name: String,
  ) : SocialImageState(name)

  public companion object {
    public val entries: List<SocialImageState>
      get() = listOf(PENDING, READY, FAILED)

    public fun valueOf(name: String): SocialImageState = when (name) {
      "PENDING" -> PENDING
      "READY" -> READY
      "FAILED" -> FAILED
      else -> Unknown(name)
    }
  }
}

public object SocialImageDateStyleSerializer : KSerializer<SocialImageDateStyle> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("SocialImageDateStyle", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: SocialImageDateStyle) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): SocialImageDateStyle = SocialImageDateStyle.valueOf(decoder.decodeString())
}

@Serializable(with = SocialImageDateStyleSerializer::class)
public sealed class SocialImageDateStyle(
  public val name: String,
) {
  override fun toString(): String = name

  public object DEFAULT : SocialImageDateStyle("DEFAULT")

  public object TODAY : SocialImageDateStyle("TODAY")

  public object TONIGHT : SocialImageDateStyle("TONIGHT")

  public object DAY_OF_WEEK : SocialImageDateStyle("DAY_OF_WEEK")

  public class Unknown(
    name: String,
  ) : SocialImageDateStyle(name)

  public companion object {
    public val entries: List<SocialImageDateStyle>
      get() = listOf(DEFAULT, TODAY, TONIGHT, DAY_OF_WEEK)

    public fun valueOf(name: String): SocialImageDateStyle = when (name) {
      "DEFAULT" -> DEFAULT
      "TODAY" -> TODAY
      "TONIGHT" -> TONIGHT
      "DAY_OF_WEEK" -> DAY_OF_WEEK
      else -> Unknown(name)
    }
  }
}

public object SocialImageRendererSerializer : KSerializer<SocialImageRenderer> {
  override val descriptor: SerialDescriptor =
      PrimitiveSerialDescriptor("SocialImageRenderer", PrimitiveKind.STRING)

  override fun serialize(encoder: Encoder, `value`: SocialImageRenderer) {
    encoder.encodeString(value.name)
  }

  override fun deserialize(decoder: Decoder): SocialImageRenderer = SocialImageRenderer.valueOf(decoder.decodeString())
}

@Serializable(with = SocialImageRendererSerializer::class)
public sealed class SocialImageRenderer(
  public val name: String,
) {
  override fun toString(): String = name

  public object OPENAI_GPT_IMAGE_2 : SocialImageRenderer("OPENAI_GPT_IMAGE_2")

  public object GEMINI_NANO_BANANA_PRO : SocialImageRenderer("GEMINI_NANO_BANANA_PRO")

  public object TEMPLATE : SocialImageRenderer("TEMPLATE")

  public class Unknown(
    name: String,
  ) : SocialImageRenderer(name)

  public companion object {
    public val entries: List<SocialImageRenderer>
      get() = listOf(OPENAI_GPT_IMAGE_2, GEMINI_NANO_BANANA_PRO, TEMPLATE)

    public fun valueOf(name: String): SocialImageRenderer = when (name) {
      "OPENAI_GPT_IMAGE_2" -> OPENAI_GPT_IMAGE_2
      "GEMINI_NANO_BANANA_PRO" -> GEMINI_NANO_BANANA_PRO
      "TEMPLATE" -> TEMPLATE
      else -> Unknown(name)
    }
  }
}

public typealias AWSDate = LocalDate

public typealias AWSTime = LocalTime

public typealias AWSDateTime = Instant

public typealias EventDuration = Duration

public val serializerModule: SerializersModule = SerializersModule {
  contextual(LocalDate::class, LocalDateIso8601Serializer)
  contextual(LocalTime::class, LocalTimeIso8601Serializer)
  contextual(Instant::class, InstantIso8601Serializer)
  contextual(Duration::class, DurationSerializer)
}

internal val json: Json = Json {
  serializersModule = serializerModule
  ignoreUnknownKeys = true
}
